import { useState, useCallback, useMemo, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useCommunity } from "@/contexts/CommunityContext";
import { QRScanner } from "@/components/guard/QRScanner";
import { ScanResult } from "@/components/guard/ScanResult";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ScanLine,
  CheckCircle2,
  Loader2,
  Search,
  User,
  LogIn,
  LogOut,
  Home,
  Clock,
  List,
} from "lucide-react";
import { format, isToday, isYesterday, startOfDay } from "date-fns";

interface ValidateResult {
  valid: boolean;
  reason?: string;
  invitation: {
    id: string;
    guestName: string | null;
    guestDni: string | null;
    category: string;
    unit: {
      unitNumber: string;
      blockSection: string | null;
    };
    hostName: string | null;
    validFrom: string;
    validUntil: string | null;
    suggestedDirection: string;
    lastAccessTime: string | null;
    lastAccessDirection: string | null;
  } | null;
}

type ScanState =
  | { type: "scanning" }
  | { type: "loading"; token: string }
  | { type: "preview"; result: ValidateResult }
  | {
      type: "success";
      guestName: string | null;
      direction: "INBOUND" | "OUTBOUND";
    }
  | { type: "error"; message: string };

type AccessLogItem = {
  id: string;
  guestName: string | null;
  guestDni: string | null;
  category: "TEMPORARY" | "PERMANENT" | "SERVICE";
  status: "ACTIVE" | "REVOKED" | "EXPIRED";
  unit: {
    unitNumber: string;
    blockSection: string | null;
  };
  hostName: string | null;
  validFrom: string;
  validUntil: string | null;
  createdAt: string;
  entryStatus: "inside" | "outside" | "never_entered";
  lastAccessTime: string | null;
  lastAccessDirection: "INBOUND" | "OUTBOUND" | null;
};

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Ahora";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return format(date, "MMM d");
}

function getDateLabel(date: Date): string {
  if (isToday(date)) return "Hoy";
  if (isYesterday(date)) return "Ayer";
  return format(date, "MMMM d, yyyy");
}

function groupByDay(
  items: AccessLogItem[],
): { label: string; date: Date; items: AccessLogItem[] }[] {
  const groups = new Map<string, { date: Date; items: AccessLogItem[] }>();

  for (const item of items) {
    const dateToUse = item.lastAccessTime || item.createdAt;
    const day = startOfDay(new Date(dateToUse));
    const key = day.toISOString();

    if (!groups.has(key)) {
      groups.set(key, { date: day, items: [] });
    }
    groups.get(key)!.items.push(item);
  }

  return Array.from(groups.entries())
    .sort(([, a], [, b]) => b.date.getTime() - a.date.getTime())
    .map(([, group]) => ({
      label: getDateLabel(group.date),
      date: group.date,
      items: group.items,
    }));
}

function EntryStatusBadge({
  status,
}: {
  status: "inside" | "outside" | "never_entered";
}) {
  switch (status) {
    case "inside":
      return (
        <Badge variant="outline" className="border-green-500 text-green-600">
          <LogIn className="mr-1 h-3 w-3" />
          Adentro
        </Badge>
      );
    case "outside":
      return (
        <Badge variant="outline" className="border-orange-500 text-orange-600">
          <LogOut className="mr-1 h-3 w-3" />
          Afuera
        </Badge>
      );
    case "never_entered":
      return <Badge variant="secondary">Sin ingreso</Badge>;
  }
}

export function GuardAccessPage() {
  const { selectedCommunityId } = useCommunity();
  const { data: communities } = trpc.amenity.getUserCommunities.useQuery();
  const effectiveCommunityId = selectedCommunityId ?? communities?.[0]?.id;
  const [activeTab, setActiveTab] = useState("scan");

  // Scan tab state
  const [scanState, setScanState] = useState<ScanState>({ type: "scanning" });

  // Log tab state
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const utils = trpc.useUtils();

  // Scan tab queries/mutations
  const validateMutation = trpc.access.validateQRToken.useQuery(
    {
      qrToken: scanState.type === "loading" ? scanState.token : "",
      communityId: effectiveCommunityId || "",
    },
    {
      enabled: scanState.type === "loading" && !!effectiveCommunityId,
      retry: false,
    },
  );

  const logAccessMutation = trpc.access.logAccess.useMutation({
    onSuccess: (data) => {
      setScanState({
        type: "success",
        guestName: data.guestName,
        direction: data.direction,
      });
      utils.access.invalidate();
    },
    onError: (error) => {
      setScanState({ type: "error", message: error.message });
    },
  });

  // Log tab query
  const accessLogQuery = trpc.access.getAccessLog.useInfiniteQuery(
    {
      communityId: effectiveCommunityId || "",
      search: debouncedSearch || undefined,
      limit: 50,
    },
    {
      enabled: !!effectiveCommunityId && activeTab === "log",
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    },
  );

  // Manual entry/exit mutation for log tab
  const manualLogMutation = trpc.access.logAccess.useMutation({
    onSuccess: () => {
      utils.access.getAccessLog.invalidate();
    },
  });

  // Handle validation result for scan tab
  if (
    scanState.type === "loading" &&
    validateMutation.data &&
    !validateMutation.isLoading
  ) {
    setScanState({
      type: "preview",
      result: validateMutation.data,
    });
  }

  if (
    scanState.type === "loading" &&
    validateMutation.error &&
    !validateMutation.isLoading
  ) {
    setScanState({
      type: "error",
      message: validateMutation.error.message,
    });
  }

  const handleScan = useCallback((token: string) => {
    setScanState({ type: "loading", token });
  }, []);

  const handleLogAccess = useCallback(
    (direction: "INBOUND" | "OUTBOUND") => {
      if (
        scanState.type !== "preview" ||
        !scanState.result.invitation ||
        !effectiveCommunityId
      ) {
        return;
      }

      logAccessMutation.mutate({
        communityId: effectiveCommunityId,
        invitationId: scanState.result.invitation.id,
        direction,
      });
    },
    [scanState, effectiveCommunityId, logAccessMutation],
  );

  const handleReset = useCallback(() => {
    setScanState({ type: "scanning" });
  }, []);

  const handleManualLog = useCallback(
    (invitationId: string, direction: "INBOUND" | "OUTBOUND") => {
      if (!effectiveCommunityId) return;
      manualLogMutation.mutate({
        communityId: effectiveCommunityId,
        invitationId,
        direction,
        method: "MANUAL_ENTRY",
      });
    },
    [effectiveCommunityId, manualLogMutation],
  );

  // Group log items by day
  const pages = accessLogQuery.data?.pages;
  const groupedItems = useMemo(() => {
    if (!pages) return [];
    const allItems = pages.flatMap((page) => page.items) as AccessLogItem[];
    return groupByDay(allItems);
  }, [pages]);

  if (!effectiveCommunityId) {
    return (
      <div className="container mx-auto max-w-2xl px-4 py-6 md:px-6 md:py-10">
        <Alert>
          <AlertDescription>
            Seleccioná un barrio para acceder a las funciones de seguridad.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Acceso</h1>
        <p className="text-muted-foreground">
          Escaneá códigos QR o gestioná el acceso de invitados
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 w-full">
          <TabsTrigger value="scan" className="flex-1">
            <ScanLine className="mr-2 h-4 w-4" />
            Escanear
          </TabsTrigger>
          <TabsTrigger value="log" className="flex-1">
            <List className="mr-2 h-4 w-4" />
            Registro
          </TabsTrigger>
        </TabsList>

        <TabsContent value="scan">
          {scanState.type === "scanning" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ScanLine className="h-5 w-5" />
                  Escanear código QR
                </CardTitle>
              </CardHeader>
              <CardContent>
                <QRScanner
                  onScan={handleScan}
                  isActive={activeTab === "scan"}
                />
                <p className="mt-4 text-center text-sm text-muted-foreground">
                  Posicioná el código QR dentro del marco
                </p>
              </CardContent>
            </Card>
          )}

          {scanState.type === "loading" && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Loader2 className="mb-4 h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Validando código QR...</p>
              </CardContent>
            </Card>
          )}

          {scanState.type === "preview" && (
            <ScanResult
              valid={scanState.result.valid}
              reason={scanState.result.reason}
              invitation={scanState.result.invitation}
              onLogAccess={handleLogAccess}
              onReset={handleReset}
              isLogging={logAccessMutation.isPending}
            />
          )}

          {scanState.type === "success" && (
            <Card className="border-green-500">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle2 className="mb-4 h-12 w-12 text-green-500" />
                <h2 className="mb-2 text-xl font-semibold">
                  Acceso registrado
                </h2>
                <p className="mb-6 text-muted-foreground">
                  {scanState.guestName}{" "}
                  {scanState.direction === "INBOUND" ? "ingresó" : "egresó"}{" "}
                  exitosamente
                </p>
                <Button onClick={handleReset}>Escanear siguiente</Button>
              </CardContent>
            </Card>
          )}

          {scanState.type === "error" && (
            <Card className="border-destructive">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Alert variant="destructive" className="mb-4">
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{scanState.message}</AlertDescription>
                </Alert>
                <Button onClick={handleReset} variant="outline">
                  Reintentar
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="log">
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o DNI..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {accessLogQuery.isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {accessLogQuery.isError && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {accessLogQuery.error.message}
              </AlertDescription>
            </Alert>
          )}

          {!accessLogQuery.isLoading && groupedItems.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <User className="mb-4 h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {search
                    ? "No se encontraron invitados con esa búsqueda"
                    : "No hay invitaciones todavía"}
                </p>
              </CardContent>
            </Card>
          )}

          {groupedItems.map((group) => (
            <div key={group.label} className="mb-6">
              <h3 className="mb-3 text-sm font-medium text-muted-foreground">
                {group.label}
              </h3>
              <div className="divide-y rounded-xl border">
                {group.items.map((item) => {
                  const unitDisplay = item.unit.blockSection
                    ? `${item.unit.blockSection} - ${item.unit.unitNumber}`
                    : item.unit.unitNumber;

                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                          <User className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {item.guestName || "Invitado"}
                            </span>
                            <EntryStatusBadge status={item.entryStatus} />
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                            {item.guestDni && <span>DNI: {item.guestDni}</span>}
                            <span className="flex items-center gap-1">
                              <Home className="h-3 w-3" />
                              {unitDisplay}
                            </span>
                            {item.lastAccessTime && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatRelativeTime(
                                  new Date(item.lastAccessTime),
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {item.entryStatus === "inside" ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <LogOut className="mr-2 h-4 w-4" />
                                Egreso
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  ¿Registrar egreso de {item.guestName}?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Se registrará que el invitado salió del
                                  barrio.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    handleManualLog(item.id, "OUTBOUND")
                                  }
                                  disabled={
                                    manualLogMutation.isPending &&
                                    manualLogMutation.variables
                                      ?.invitationId === item.id
                                  }
                                >
                                  {manualLogMutation.isPending &&
                                  manualLogMutation.variables?.invitationId ===
                                    item.id ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  ) : null}
                                  Confirmar egreso
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <LogIn className="mr-2 h-4 w-4" />
                                Ingreso
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  ¿Registrar ingreso de {item.guestName}?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Se registrará que el invitado ingresó al
                                  barrio.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    handleManualLog(item.id, "INBOUND")
                                  }
                                  disabled={
                                    manualLogMutation.isPending &&
                                    manualLogMutation.variables
                                      ?.invitationId === item.id
                                  }
                                >
                                  {manualLogMutation.isPending &&
                                  manualLogMutation.variables?.invitationId ===
                                    item.id ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  ) : null}
                                  Confirmar ingreso
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {accessLogQuery.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => accessLogQuery.fetchNextPage()}
                disabled={accessLogQuery.isFetchingNextPage}
              >
                {accessLogQuery.isFetchingNextPage ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cargando...
                  </>
                ) : (
                  "Cargar más"
                )}
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
